import { default as MaterialButton } from '@material-ui/core/Button';
import React, { MouseEventHandler } from 'react';
import styled, { css, FlattenInterpolation } from 'styled-components';
import { Color } from '~common/model/basics';
import {
  ButtonType,
  getColorStyle,
  getDisabledColorStyle,
  getFontSize,
  getMinHeight,
  getPadding,
  Size,
} from '~common/components/button/button.helper';

export interface ButtonProps {
  onClick?: MouseEventHandler<HTMLElement>;
  type?: ButtonType;
  color?: Color;
  colorTransparency?: number;
  component?: React.ElementType<ButtonProps>;
  disabled?: boolean;
  disableFocusRipple?: boolean;
  disableRipple?: boolean;
  fullWidth?: boolean;
  size?: Size;
  textColor?: Color;
  variant?: 'text' | 'outlined' | 'contained';
  classes?: { root: string; label: string; disabled: string; fullWidth: string };
  id?: string;
}

export const Button: React.FunctionComponent<ButtonProps> = ({
  children,
  color = Color.Purple,
  colorTransparency,
  component,
  disableFocusRipple,
  disableRipple,
  fullWidth,
  size = Size.Medium,
  variant,
  textColor,
  type = ButtonType.BUTTON,
  ...rest
}) => (
  <StyledButton
    classes={{ root: 'root', label: 'label', disabled: 'disabled', fullWidth: 'fullWidth' }}
    color={color}
    colorTransparency={colorTransparency}
    component={component}
    disableFocusRipple={disableFocusRipple}
    disableRipple={disableRipple}
    fullWidth={fullWidth}
    size={size}
    variant={variant || 'contained'}
    textColor={textColor}
    type={type}
    {...rest}
  >
    {children}
  </StyledButton>
);

interface StylingProps {
  color: Color;
  colorTransparency?: number;
  textColor?: Color;
}

const StyledButton = styled(({ color, colorTransparency, textColor, ...rest }) => (
  <MaterialButton {...rest} />
))<StylingProps>`
  && {
    border-radius: 30px;
    box-shadow: none;
    min-height: ${(props): string => getMinHeight(props.size)};
    padding: ${(props): string => getPadding(props.size, props.fullWidth)};
    ${(props): FlattenInterpolation<StylingProps> | null =>
      props.fullWidth &&
      css`
        width: 100%;
      `};
    ${(props): FlattenInterpolation<StylingProps> =>
      getColorStyle(props.color, props.colorTransparency, props.textColor)};
  }

  &&:hover {
    opacity: 0.8;
    box-shadow: none;
    ${(props): FlattenInterpolation<StylingProps> =>
      getColorStyle(props.color, props.colorTransparency, props.textColor)};
  }

  &&.disabled,
  &&:hover.disabled {
    ${(props): FlattenInterpolation<StylingProps> =>
      getDisabledColorStyle(props.color, props.colorTransparency, props.textColor)};
  }

  && > span {
    font-weight: bold;
    letter-spacing: 0.8px;
    text-transform: none;
    font-size: ${(props): string => getFontSize(props.size)};
  }
  && .label {
    max-width: 100%;
    text-align: center;
    overflow-wrap: anywhere;
  }
`;



// WEBPACK FOOTER //
// /codebuild/output/src332225586/src/modules/common/src/components/button/button.component.tsx